/**
 * Authentication Utilities
 *
 * Token management for SSO authentication
 */

import Cookies from 'js-cookie';

import { getAuthCookieDomain } from '../config/authDomain';

// Spec §4.4 + architectureDesign §4.4 call for `ct_v3_access_token`. The cookie
// key stays `accessToken` since that's what the central SSO writes; we only
// control the localStorage backup key.
const TOKEN_KEY = 'ct_v3_access_token';
const COOKIE_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const COOKIE_EXPIRY_DAYS = 3;

/**
 * Get access token from cookie (primary) or localStorage (fallback)
 */
export const getAccessToken = (): string | null => {
  // Try cookie first (more secure)
  const cookieToken = Cookies.get(COOKIE_KEY);
  if (cookieToken) {
    return cookieToken;
  }

  // Fallback to localStorage
  const localToken = localStorage.getItem(TOKEN_KEY);
  return localToken;
};

/**
 * Store access token in both cookie and localStorage
 */
export const setAccessToken = (token: string): void => {
  const domain = getAuthCookieDomain();
  // Secure cookie set on the configured SSO domain (cross-subdomain in prod,
  // host-only in localhost). `secure` is also conditional on having a domain —
  // browsers reject `Secure` cookies on http://localhost.
  Cookies.set(COOKIE_KEY, token, {
    expires: COOKIE_EXPIRY_DAYS,
    ...(domain ? { domain, secure: true, sameSite: 'strict' } : { sameSite: 'lax' }),
  });

  // Backup in localStorage (spec §4.4 key: ct_v3_access_token).
  localStorage.setItem(TOKEN_KEY, token);
};

/**
 * Remove refresh token from localStorage
 */
export const removeRefreshToken = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Remove access token from both storage locations
 */
export const removeAccessToken = (): void => {
  const domain = getAuthCookieDomain();
  if (domain) Cookies.remove(COOKIE_KEY, { domain });
  Cookies.remove(COOKIE_KEY); // Also remove without domain (host-only cookie).
  localStorage.removeItem(TOKEN_KEY);
  // One-time cleanup of the v1 backup key. Safe to remove once we're sure
  // no environment still has the old localStorage entry around.
  localStorage.removeItem('smartai_auth_token');
  removeRefreshToken();
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAccessToken();
};

/**
 * Parse JWT token to extract user info (without verification)
 * Note: This is for UI purposes only. Server must always verify tokens.
 */
export const parseToken = (token: string): any | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse token:', error);
    return null;
  }
};

/**
 * Get user info from stored token
 */
export const getUserFromToken = (): any | null => {
  const token = getAccessToken();
  if (!token) return null;
  return parseToken(token);
};

/**
 * Stable user identifier for the provisional `x-user-id` header the v3 backend
 * uses to scope data (Path C handoff §2 — JWT/`sub` binding lands in a later
 * platform auth slice). Reads the JWT `sub` claim, falling back to `userId`/`id`
 * for non-standard token shapes. Returns null when unauthenticated.
 */
export const getUserId = (): string | null => {
  const payload = getUserFromToken();
  if (!payload) return null;
  return payload.sub ?? payload.userId ?? payload.id ?? null;
};

/**
 * App ids this client answers to, as they appear in a central token's `apps` claim.
 *
 * The claim's vocabulary is owned by the central identity app, not by us. It issues
 * `fashion-weaver`, `story-teller`, `kid-journey`, `creative-studio` — and refers to
 * this product as IMAGE_LAB (imagelab.creativetoolbox.ai). It has never issued
 * `creative-toolbox`, which is what this check originally demanded: the result was
 * that every real token was rejected and users bounced straight back to the login
 * page. Several plausible ids are accepted, and `VITE_APP_ACCESS_IDS` overrides the
 * list without a code change when the exact slug is confirmed.
 */
export const APP_ACCESS_IDS: string[] = String(
  import.meta.env.VITE_APP_ACCESS_IDS?.trim() || 'image-lab,creative-toolbox,creative-studio',
)
  .split(',')
  .map((s: string) => s.trim())
  .filter(Boolean);

/** Kept for callers that want the primary id. */
export const APP_NAME = APP_ACCESS_IDS[0];

/**
 * Whether a mismatching `apps` claim should DENY access.
 *
 * Off by default, deliberately. The real security boundary is the backend, which
 * validates the JWT signature and scopes every row to the token's `sub`; this check
 * is defence-in-depth and a UX affordance. Getting the slug wrong should not be able
 * to lock the whole product out — which is exactly what happened. With enforcement
 * off a mismatch is logged loudly and access continues; set
 * `VITE_ENFORCE_APP_CLAIM=true` once the correct id is confirmed.
 */
export const enforceAppClaim = (): boolean =>
  import.meta.env.VITE_ENFORCE_APP_CLAIM?.trim().toLowerCase() === 'true';

/**
 * App-access check for central-ecosystem tokens (SMA-8). Returns false only when an
 * `apps` claim is PRESENT, excludes every id in `appNames`, AND enforcement is on.
 * Dev-bypass tokens and tokens with no `apps` claim always pass.
 */
export const tokenGrantsApp = (payload: any, appNames: string[] = APP_ACCESS_IDS): boolean => {
  if (!payload) return false;
  if (payload.iss === 'dev-bypass') return true;
  const apps = payload.apps;
  if (apps == null || apps === '') return true; // no claim → allow
  const list: unknown[] = Array.isArray(apps) ? apps : String(apps).split(',');
  const granted = list.map((s: unknown) => String(s).trim());
  const matches = granted.some(g => appNames.includes(g));
  if (matches) return true;

  if (!enforceAppClaim()) {
    console.warn(
      `Token's apps claim [${granted.join(', ')}] matches none of [${appNames.join(', ')}]. ` +
        'Allowing access — the backend remains authoritative. Set VITE_ENFORCE_APP_CLAIM=true to deny.',
    );
    return true;
  }
  return false;
};

/**
 * Get refresh token from localStorage
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Store refresh token in localStorage
 */
export const setRefreshToken = (refreshToken: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

/**
 * Check if token is expired (JWT)
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = parseToken(token);
    if (!payload || !payload.exp) return true;

    // exp is in seconds, Date.now() is in milliseconds
    return Date.now() >= payload.exp * 1000;
  } catch (error) {
    console.error('Failed to check token expiration:', error);
    return true;
  }
};
